import { useMemo } from "react";
import {
  buildDailyTotals,
  formatExpectedHours,
  toIsoDateLocal,
  type HeroUsageRow,
} from "@/lib/auswertungen";

interface ChartPoint {
  isoDate: string;
  dayLabel: string;
  soll: number;
  ist: number;
}

function buildDaySeries(
  from: string,
  to: string,
  rows: HeroUsageRow[],
  expectedHoursByDay: Map<string, number> | null,
): ChartPoint[] {
  const fromDate = new Date(`${from}T00:00:00`);
  const toDate = new Date(`${to}T00:00:00`);
  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime()) || fromDate > toDate) {
    return [];
  }

  const dailyTotals = buildDailyTotals(rows);
  const points: ChartPoint[] = [];
  // Kalendertage lokal iterieren (Mitternacht → setDate), damit kein Tag durch
  // DST oder UTC-Verschiebung übersprungen/verdoppelt wird.
  const cursor = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  const end = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
  while (cursor.getTime() <= end.getTime()) {
    const isoDate = toIsoDateLocal(cursor);
    const writtenSeconds = dailyTotals.get(isoDate)?.writtenSeconds ?? 0;
    points.push({
      isoDate,
      dayLabel: String(cursor.getDate()),
      soll: expectedHoursByDay?.get(isoDate) ?? 0,
      ist: writtenSeconds / 3600,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return points;
}

function niceMax(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 8;
  const padded = value * 1.1;
  const magnitude = 10 ** Math.floor(Math.log10(padded));
  const normalized = padded / magnitude;
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function polylinePoints(
  values: number[],
  maxY: number,
  plotLeft: number,
  plotTop: number,
  plotWidth: number,
  plotHeight: number,
): string {
  if (values.length === 0) return "";
  const n = values.length;
  return values
    .map((value, index) => {
      const x =
        n === 1
          ? plotLeft + plotWidth / 2
          : plotLeft + (index / (n - 1)) * plotWidth;
      const ratio = maxY > 0 ? Math.max(0, Math.min(value, maxY)) / maxY : 0;
      const y = plotTop + plotHeight - ratio * plotHeight;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

export function SollIstChart({
  rows,
  from,
  to,
  expectedHoursByDay,
}: {
  rows: HeroUsageRow[];
  from: string;
  to: string;
  expectedHoursByDay: Map<string, number> | null;
}) {
  const points = useMemo(
    () => buildDaySeries(from, to, rows, expectedHoursByDay),
    [from, to, rows, expectedHoursByDay],
  );

  const { maxY, sollTotal, istTotal } = useMemo(() => {
    let max = 0;
    let sollSum = 0;
    let istSum = 0;
    for (const point of points) {
      max = Math.max(max, point.soll, point.ist);
      sollSum += point.soll;
      istSum += point.ist;
    }
    return { maxY: niceMax(max), sollTotal: sollSum, istTotal: istSum };
  }, [points]);

  if (points.length === 0) {
    return null;
  }

  const width = 720;
  const height = 240;
  const padLeft = 44;
  const padRight = 16;
  const padTop = 16;
  const padBottom = 36;
  const plotLeft = padLeft;
  const plotTop = padTop;
  const plotWidth = width - padLeft - padRight;
  const plotHeight = height - padTop - padBottom;

  const sollLine = polylinePoints(
    points.map((p) => p.soll),
    maxY,
    plotLeft,
    plotTop,
    plotWidth,
    plotHeight,
  );
  const istLine = polylinePoints(
    points.map((p) => p.ist),
    maxY,
    plotLeft,
    plotTop,
    plotWidth,
    plotHeight,
  );

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
    ratio,
    value: maxY * ratio,
    y: plotTop + plotHeight - ratio * plotHeight,
  }));

  const labelStep = points.length > 20 ? 2 : 1;

  return (
    <section className="eh-soll-ist-chart">
      <div className="eh-soll-ist-chart__header">
        <h3 className="eh-soll-ist-chart__title">Soll / Ist im Monat</h3>
        <div className="eh-soll-ist-chart__legend">
          <span className="eh-soll-ist-chart__legend-item eh-soll-ist-chart__legend-item--soll">
            Soll · Σ {formatExpectedHours(sollTotal)}
          </span>
          <span className="eh-soll-ist-chart__legend-item eh-soll-ist-chart__legend-item--ist">
            Ist · Σ {formatExpectedHours(istTotal)}
          </span>
        </div>
      </div>
      <div className="eh-soll-ist-chart__svg-wrap">
        <svg
          className="eh-soll-ist-chart__svg"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Liniendiagramm Soll- und Ist-Stunden pro Tag"
        >
          {yTicks.map((tick) => (
            <g key={tick.ratio}>
              <line
                className="eh-soll-ist-chart__grid"
                x1={plotLeft}
                y1={tick.y}
                x2={plotLeft + plotWidth}
                y2={tick.y}
              />
              <text
                className="eh-soll-ist-chart__axis-label"
                x={plotLeft - 8}
                y={tick.y + 3}
                textAnchor="end"
              >
                {tick.value.toFixed(0)}
              </text>
            </g>
          ))}

          <polyline className="eh-soll-ist-chart__line eh-soll-ist-chart__line--soll" points={sollLine} />
          <polyline className="eh-soll-ist-chart__line eh-soll-ist-chart__line--ist" points={istLine} />

          {points.map((point, index) => {
            const x =
              points.length === 1
                ? plotLeft + plotWidth / 2
                : plotLeft + (index / (points.length - 1)) * plotWidth;
            const sollRatio = maxY > 0 ? Math.max(0, Math.min(point.soll, maxY)) / maxY : 0;
            const istRatio = maxY > 0 ? Math.max(0, Math.min(point.ist, maxY)) / maxY : 0;
            const sollY = plotTop + plotHeight - sollRatio * plotHeight;
            const istY = plotTop + plotHeight - istRatio * plotHeight;
            const showLabel = index % labelStep === 0 || index === points.length - 1;
            return (
              <g key={point.isoDate}>
                <circle
                  className="eh-soll-ist-chart__dot eh-soll-ist-chart__dot--soll"
                  cx={x}
                  cy={sollY}
                  r={2.5}
                >
                  <title>
                    {point.isoDate}: Soll {formatExpectedHours(point.soll)}
                  </title>
                </circle>
                <circle
                  className="eh-soll-ist-chart__dot eh-soll-ist-chart__dot--ist"
                  cx={x}
                  cy={istY}
                  r={2.5}
                >
                  <title>
                    {point.isoDate}: Ist {formatExpectedHours(point.ist)}
                  </title>
                </circle>
                {showLabel ? (
                  <text
                    className="eh-soll-ist-chart__axis-label"
                    x={x}
                    y={height - 12}
                    textAnchor="middle"
                  >
                    {point.dayLabel}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>
    </section>
  );
}
